import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, Route, Routes } from 'react-router-dom';

import { CreditLimits } from './components/credit-limits';
import { Integrations } from './components/integrations';
import { Roles } from './components/roles';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';

enum ConfigurationTab {
  Credits = 'credits',
  Integrations = 'integrations',
  Roles = 'roles',
}

const tabs = [
  {
    id: ConfigurationTab.Credits,
    permission: Permission.TokenLimitsManage,
    title: 'Credit Limits',
    icon: <Icon className="lucide--coins" />,
  },
  {
    id: ConfigurationTab.Integrations,
    permission: Permission.IntegrationsManage,
    title: 'Integrations',
    icon: <Icon className="lucide--unplug" />,
  },
  {
    id: ConfigurationTab.Roles,
    permission: Permission.UsersManage,
    title: 'Roles & Permissions',
    icon: <Icon className="lucide--shield-user" />,
  },
];

export function OrganisationSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useHasPermission();

  // Get the active tab from the URL
  const activeTab = useMemo(() => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    return lastSegment as ConfigurationTab;
  }, [location.pathname]);

  return (
    <>
      <Breadcrumb
        items={[
          {
            href: '/organisation-settings/credits',
            label: `${capitalizeFirstLetter(t('organisation'))} Settings`,
            active: true,
          },
        ]}
      />
      <PageContainer>
        <PageHeader>
          <PageTitle text={`${capitalizeFirstLetter(t('organisation'))} Settings`} />
        </PageHeader>
        <Tabs className="mb-2" value={activeTab} onValueChange={(tab) => navigate(tab)}>
          <TabsList className="w-full justify-start">
            {tabs.map((tab) => (
              <>
                {hasPermission(tab.permission) && (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="flex items-center gap-2 font-serif text-base font-medium text-gray-700 data-[state=active]:border-teal-500 data-[state=active]:text-teal-500"
                  >
                    {tab.icon}
                    {tab.title}
                  </TabsTrigger>
                )}
              </>
            ))}
          </TabsList>
        </Tabs>

        <div className="w-full">
          <Routes>
            {<Route path="credits" element={<CreditLimits />} />}
            {<Route path="integrations" element={<Integrations />} />}
            {<Route path="roles" element={<Roles />} />}
          </Routes>
        </div>
      </PageContainer>
    </>
  );
}
